'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import { AlertModal, ConfirmModal } from '@/components/ui/modal';

interface AlertOptions {
  title: string;
  message?: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
}

interface ConfirmOptions {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

interface ModalContextType {
  showAlert: (options: AlertOptions) => void;
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [alertState, setAlertState] = useState<(AlertOptions & { isOpen: boolean }) | null>(null);
  const [confirmState, setConfirmState] = useState<(ConfirmOptions & { isOpen: boolean }) | null>(null);
  const [confirmResolver, setConfirmResolver] = useState<((value: boolean) => void) | null>(null);

  const showAlert = (options: AlertOptions) => {
    setAlertState({ ...options, isOpen: true });
  };

  const showConfirm = (options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({ ...options, isOpen: true });
      setConfirmResolver(() => resolve);
    });
  };

  const handleAlertClose = () => {
    setAlertState(null);
  };

  const handleConfirmClose = () => {
    if (confirmResolver) {
      confirmResolver(false);
      setConfirmResolver(null);
    }
    setConfirmState(null);
  };

  const handleConfirm = () => {
    if (confirmResolver) {
      confirmResolver(true);
      setConfirmResolver(null);
    }
  };

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      
      {alertState && (
        <AlertModal
          isOpen={alertState.isOpen}
          onClose={handleAlertClose}
          title={alertState.title}
          message={alertState.message ?? ''}
          variant={alertState.variant}
        />
      )}

      {confirmState && (
        <ConfirmModal
          isOpen={confirmState.isOpen}
          onClose={handleConfirmClose}
          onConfirm={handleConfirm}
          title={confirmState.title}
          description={confirmState.description}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          variant={confirmState.variant}
        />
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

